import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import ClickOutside from 'react-click-outside';
import moment from 'moment-timezone';
import cx from 'classnames';
import VisibilitySensor from 'react-visibility-sensor';

import { TIME_FORMAT, TIMEPICKER_START_IMPRESSION, TIMEPICKER_START_CLICK, TIMEPICKER_END_IMPRESSION, TIMEPICKER_END_CLICK } from 'lib/analytics/events';
import { PARKING_UNAVAILABLE_MESSAGE } from 'components/checkout/modal-notices';

import DatePicker from 'components/common/date-picker';
import TimeCollection from 'components/common/time-date-picker/time-collection';

const propTypes = {
  time: PropTypes.instanceOf(moment).isRequired,
  timezone: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  dateChange: PropTypes.func.isRequired,
  timeChange: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,

  checkoutError: PropTypes.instanceOf(Map),
  label: PropTypes.string,
  displayTimes: PropTypes.bool,
  hideOnDateChange: PropTypes.bool,
};

const defaultProps = {
  label: '',
  displayTimes: true,
  hideOnDateChange: false,
  checkoutError: Map(),
};

const LIST_ID = 'time-selector-list';

class DateTimeSelector extends Component {
  static scrollList(distance) {
    document.getElementById(LIST_ID).scrollTop += distance;
  }

  constructor(props) {
    super(props);

    this.state = {
      showSelector: false,
      dateChanged: false,
      timeChanged: false,
      pickerViewed: false,
    };

    this.showSelector = this.showSelector.bind(this);
    this.hideSelector = this.hideSelector.bind(this);
    this.toggleSelector = this.toggleSelector.bind(this);
    this.selectedDateChanged = this.selectedDateChanged.bind(this);
    this.selectedTimeChanged = this.selectedTimeChanged.bind(this);
    this.shouldHideSelector = this.shouldHideSelector.bind(this);
    this.renderTimeCollection = this.renderTimeCollection.bind(this);
    this.onSelectorVisibilityChange = this.onSelectorVisibilityChange.bind(this);
    this.onDateTimeClicked = this.onDateTimeClicked.bind(this);
  }

  get clickEvent() {
    if (this.props.field === 'endTime') {
      return TIMEPICKER_END_CLICK;
    }
    return TIMEPICKER_START_CLICK;
  }

  get impressionEvent() {
    if (this.props.field === 'endTime') {
      return {
        ...TIMEPICKER_END_IMPRESSION,
        properties: {
          end_time: this.props.time.format(TIME_FORMAT),
        },
      };
    }
    return {
      ...TIMEPICKER_START_IMPRESSION,
      properties: {
        start_time: this.props.time.format(TIME_FORMAT),
      },
    };
  }

  showSelector() {
    this.setState({
      showSelector: true,
    });
  }

  hideSelector() {
    if (this.props.checkoutError.get('message') === PARKING_UNAVAILABLE_MESSAGE) { return; }

    this.setState({
      showSelector: false,
      dateChanged: false,
      timeChanged: false,
    });
  }

  toggleSelector() {
    this.setState({
      showSelector: !this.state.showSelector,
    });
  }

  selectedDateChanged(value) {
    if (this.props.dateChange instanceof Function) {
      this.props.dateChange(value);
      this.setState({ dateChanged: true }, () => {
        if (this.shouldHideSelector()) {
          this.hideSelector();
        }
      });
    }
  }

  selectedTimeChanged(value) {
    if (!(this.props.timeChange instanceof Function)) { return; }

    this.props.timeChange(value);
    this.setState({ timeChanged: true }, () => {
      if (this.shouldHideSelector()) { this.hideSelector(); }
    });
  }

  shouldHideSelector() {
    const dateTimeChanged = (this.state.dateChanged && this.state.timeChanged);
    const monthlyDateChanged = (this.props.hideOnDateChange && this.state.dateChanged);

    return dateTimeChanged || monthlyDateChanged;
  }

  onSelectorVisibilityChange(isVisible) {
    if (!this.state.pickerViewed) {
      this.setState({ pickerViewed: isVisible });
      if (isVisible) {
        this.props.trackEvent(this.impressionEvent);
      }
    }
  }

  onDateTimeClicked() {
    this.toggleSelector();
    this.props.trackEvent(this.clickEvent);
  }

  renderDateTime() {
    const { time, label } = this.props;
    const timeDateClasses = cx({
      'text-color-blue': this.state.showSelector,
    });

    return (
      <div className="text-align-sm-center row" onClick={this.toggleSelector}>
        <div className="col-xs-2 col-sm-12 text-size-12 text-weight-book">
          { label }
        </div>
        <div className={`${timeDateClasses} col-xs-5 col-sm-12 col-xs-push-5 col-sm-push-0 text-size-xs-14 text-size-sm-16 text-weight-xs-medium text-weight-sm-bold uppercase`}>
          <span className="padding-left-sm-30">{ time.format('h:mma') }</span>
          <span className="iconified-font iconified-pencil text-color-blue text-color-hover-blue margin-left-10 text-size-14 hidden-xs" />
        </div>
        <div className={`${timeDateClasses} col-xs-5 col-sm-12 col-xs-pull-5 col-sm-pull-0 col-gutter-right-xs-0 col-gutter-right-sm-15 text-size-xs-14 text-size-sm-12 text-weight-xs-medium text-weight-sm-book uppercase`}>
          { time.format('ddd, MMM D') }
        </div>
      </div>
    );
  }

  renderTimeCollection() {
    if (!this.props.displayTimes) { return null; }

    return (
      <div className="col-xs-4 text-align-center">
        <span className="iconified-font iconified-chevron-up text-color-blue text-size-10 clickable text-color-hover-blue" onClick={() => { DateTimeSelector.scrollList(-75); }} />
        <div className="horizontal-time-dropdown text-weight-light list-unstyled margin-top-10 margin-bottom-10">
          <TimeCollection
            value={this.props.time}
            timezone={this.props.timezone}
            onChange={this.selectedTimeChanged}
            lineItemClassName="clickable text-color-hover-blue"
            listId={LIST_ID}
          />
        </div>
        <span className="iconified-font iconified-chevron-down text-color-blue text-size-10 clickable text-color-hover-blue" onClick={() => { DateTimeSelector.scrollList(75); }} />
      </div>
    );
  }

  renderDateTimeSelector() {
    if (!this.state.showSelector) { return false; }

    const containerClasses = cx({
      'date-time-selector': true,
      'container': true,
      'row': true,
      'position-absolute': true,
      'text-color-dark-slate': true,
      'heavy-shadowed': true,
      'times-displayed': this.props.displayTimes,
      'date-only': !this.props.displayTimes,
    });

    return (
      <div className={containerClasses}>
        <div className="row background-color-white padding-top-20 padding-bottom-20 border-style-solid border-color-light-grey border-side-right border-side-left">
          <div className="col-xs-8 col-gutter-right-0">
            <div className="border-style-solid border-side-right border-color-light-grey">
              <DatePicker
                date={this.props.time.clone().startOf('day')}
                minDate={moment().startOf('day')}
                onDateChange={this.selectedDateChanged}
              />
            </div>
          </div>
          { this.renderTimeCollection() }
        </div>
        <div className="row">
          <div className="text-size-16 text-color-blue background-color-light-blue text-align-center padding-10 col-xs-12 clickable noselect" onClick={this.hideSelector}>
            <span className="iconified-font iconified-x margin-right-5 iconified-space-right" />
            CLOSE
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <VisibilitySensor onChange={this.onSelectorVisibilityChange} partialVisibility>
        <ClickOutside onClickOutside={this.hideSelector}>
          { this.renderDateTime() }
          { this.renderDateTimeSelector() }
        </ClickOutside>
      </VisibilitySensor>
    );
  }
}

DateTimeSelector.propTypes = propTypes;
DateTimeSelector.defaultProps = defaultProps;

export default DateTimeSelector;

